import { Component, Inject, OnInit, Optional } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ToastrService } from "ngx-toastr";
import {
  buildEmbedUrl,
  buildIframeEmbedCode,
  DEFAULT_EMBED_SIZES,
  EmbedContentType,
  EmbedContext,
  EmbedSizePreset,
  EmbedViewType,
  getEmbedPreviewBaseUrl,
  getEmbedViewOptions,
} from "src/app/utils/embed.util";
import { EmbedPreviewDialogComponent } from "./embed-preview-dialog.component";

export interface ShareEmbedDialogData {
  contentType?: EmbedContentType;
  flowData?: any;
  bundleData?: any;
  embedContext?: EmbedContext;
}

@Component({
  selector: "app-embeded",
  templateUrl: "./embeded.component.html",
  styleUrls: ["./embeded.component.scss"],
})
export class EmbededComponent implements OnInit {
  contentType: EmbedContentType = "flow";
  contentData: any;
  flowCoverImageUrl = "";
  embedUrl = "";
  codeToEmbed = "";
  shareLink = "";
  previewUrl: SafeResourceUrl;
  sizePresets = DEFAULT_EMBED_SIZES;
  selectedSize: EmbedSizePreset = DEFAULT_EMBED_SIZES[0];
  viewOptions: { id: EmbedViewType; label: string; stepNumber?: number }[] = [];
  selectedViewOption: { id: EmbedViewType; label: string; stepNumber?: number };
  customWidth = DEFAULT_EMBED_SIZES[0].width;
  customHeight = DEFAULT_EMBED_SIZES[0].height;
  useCustomSize = false;
  showLivePreview = false;

  constructor(
    public dialogRef: MatDialogRef<EmbededComponent>,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: ShareEmbedDialogData
  ) {}

  ngOnInit(): void {
    this.contentData = this.resolveContentData();
    this.contentType = this.resolveContentType();
    this.flowCoverImageUrl = this.contentData?.url
      ? `url("${this.contentData.url}")`
      : "";

    const stepCount =
      this.contentData?.steps?.length ||
      this.contentData?.stepCount ||
      0;
    const hasItems =
      (this.contentData?.components?.length || 0) > 0 ||
      (this.contentData?.itemsRequired?.length || 0) > 0 ||
      (this.contentData?.dataSource?.length || 0) > 0 ||
      !!this.contentData?.hasItems;

    this.viewOptions = getEmbedViewOptions(
      this.contentType,
      stepCount,
      hasItems
    );

    this.selectedViewOption =
      this.viewOptions.find((option) => option.id === "full") ||
      this.viewOptions[0];

    this.shareLink = this.buildShareLink();
    this.refreshEmbedCode();
  }

  close(): void {
    this.dialogRef.close();
  }

  showToast(message: string): void {
    this.toastr.success(message);
  }

  openPreview(): void {
    if (!this.previewUrl) {
      return;
    }

    this.dialog.open(EmbedPreviewDialogComponent, {
      panelClass: ["dialoguebox", "embed-preview-dialog-pane"],
      width: "760px",
      maxWidth: "100vw",
      data: {
        title: this.title,
        previewUrl: this.previewUrl,
        width: this.embedWidth,
        height: this.embedHeight,
        flowData: this.contentData,
      },
    });
  }

  onViewOptionChange(option: {
    id: EmbedViewType;
    label: string;
    stepNumber?: number;
  }): void {
    this.selectedViewOption = option;
    this.refreshEmbedCode();
  }

  onSizePresetChange(preset: EmbedSizePreset): void {
    this.selectedSize = preset;
    this.useCustomSize = false;
    this.customWidth = preset.width;
    this.customHeight = preset.height;
    this.refreshEmbedCode();
  }

  onCustomSizeChange(): void {
    this.useCustomSize = true;
    this.refreshEmbedCode();
  }

  get embedWidth(): number {
    return this.useCustomSize ? this.customWidth : this.selectedSize.width;
  }

  get embedHeight(): number {
    return this.useCustomSize ? this.customHeight : this.selectedSize.height;
  }

  get title(): string {
    return this.contentData?.title || "Share";
  }

  private resolveContentData(): any {
    if (this.dialogData?.flowData) {
      return this.dialogData.flowData;
    }
    if (this.dialogData?.bundleData) {
      return this.dialogData.bundleData;
    }
    const stored = localStorage.getItem("flowsData");
    return stored ? JSON.parse(stored) : {};
  }

  private resolveContentType(): EmbedContentType {
    if (this.dialogData?.contentType) {
      return this.dialogData.contentType;
    }
    if (this.dialogData?.bundleData) {
      return "bundle";
    }
    const type = (this.contentData?.type || "").toString().toLowerCase();
    return type === "bundle" ? "bundle" : "flow";
  }

  private getRowKey(): string {
    return (
      this.contentData?.rowKey ||
      this.contentData?.RowKey ||
      this.contentData?.id ||
      this.contentData?.flowId ||
      ""
    );
  }

  private buildShareLink(): string {
    if (typeof window !== "undefined" && window.location?.href) {
      return window.location.href.split("#")[0];
    }
    return buildEmbedUrl(
      this.contentType,
      this.getRowKey(),
      this.contentData?.title,
      "full"
    );
  }

  private refreshEmbedCode(): void {
    const rowKey = this.getRowKey();
    const view = this.selectedViewOption?.id || "full";
    const stepNumber = this.selectedViewOption?.stepNumber;

    this.embedUrl = buildEmbedUrl(
      this.contentType,
      rowKey,
      this.contentData?.title,
      view,
      stepNumber
    );
    this.codeToEmbed = buildIframeEmbedCode(
      this.embedUrl,
      this.embedWidth,
      this.embedHeight
    );
    const previewEmbedUrl = buildEmbedUrl(
      this.contentType,
      rowKey,
      this.contentData?.title,
      view,
      stepNumber,
      getEmbedPreviewBaseUrl()
    );
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      previewEmbedUrl
    );
  }

  /** Returns the full flow URL for linking */
  getFlowUrl(): string {
    const rowKey = this.getRowKey();
    if (!rowKey) return '#';
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/viewflow/${rowKey}`;
  }

  /** Opens a social sharing URL in a new window */
  shareOnSocial(platform: string): void {
    const encodedUrl = encodeURIComponent(this.shareLink);
    const encodedTitle = encodeURIComponent(this.title);
    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
    }
  }
}
